import axios from 'axios'

/**
 * Shared CSRF token state.
 *
 * The backend (`middleware/csrf.rs`) rejects every non-GET/HEAD/OPTIONS request
 * that arrives without a valid `X-CSRF-Token`, apart from a small skip list
 * (login/register/refresh, webhooks, the token endpoint itself). The token is
 * served by `GET /api/csrf-token` — note that path is *not* under `/api/v1`.
 *
 * This lives outside React so the `api` axios instance can attach the token to
 * every mutating request, including ones fired before any component mounts.
 */

const CSRF_HEADER = 'X-CSRF-Token'
const CSRF_STORAGE_KEY = 'csrf_token'
// Relative on purpose: nginx proxies /api to the backend in production and the
// vite dev server proxies it to :8080. An absolute URL would break both.
const CSRF_ENDPOINT = '/api/csrf-token'

interface CsrfTokenResponse {
  csrf_token: string
}

let cachedToken: string | null = null
// Single-flight: concurrent callers share one in-flight request.
let inFlight: Promise<string | null> | null = null

/** Read the cached token without triggering a fetch. */
export const peekCsrfToken = (): string | null => {
  if (cachedToken) return cachedToken
  try {
    cachedToken = sessionStorage.getItem(CSRF_STORAGE_KEY)
  } catch {
    // sessionStorage can throw in private-browsing/sandboxed contexts.
    cachedToken = null
  }
  return cachedToken
}

/** Drop the cached token so the next request fetches a fresh one. */
export const clearCsrfToken = (): void => {
  cachedToken = null
  inFlight = null
  try {
    sessionStorage.removeItem(CSRF_STORAGE_KEY)
  } catch {
    // ignore
  }
}

/** Fetch a new token from the backend, bypassing the cache. */
export const fetchCsrfToken = async (): Promise<string | null> => {
  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      const response = await axios.get<CsrfTokenResponse>(CSRF_ENDPOINT, {
        withCredentials: true,
      })
      const token = response.data.csrf_token
      cachedToken = token
      try {
        sessionStorage.setItem(CSRF_STORAGE_KEY, token)
      } catch {
        // ignore
      }
      return token
    } catch (err) {
      console.error('Failed to fetch CSRF token:', err)
      return null
    } finally {
      inFlight = null
    }
  })()

  return inFlight
}

/** Return a usable token, fetching one if we do not already hold it. */
export const ensureCsrfToken = async (): Promise<string | null> => {
  return peekCsrfToken() ?? (await fetchCsrfToken())
}

/** Request methods the backend exempts from CSRF checks. */
const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS']

/**
 * Only the token endpoint itself is excluded. The backend also skip-lists
 * login/register/refresh, but we still send the header there: it is ignored on
 * those routes, and attaching it uniformly avoids a second skip list that could
 * drift out of sync with `middleware/csrf.rs`.
 */
const SKIP_PATHS = ['/csrf-token']

export const requiresCsrfToken = (method?: string, url?: string): boolean => {
  if (SAFE_METHODS.includes((method ?? 'GET').toUpperCase())) return false
  if (url && SKIP_PATHS.some((path) => url.includes(path))) return false
  return true
}

export { CSRF_HEADER, CSRF_STORAGE_KEY, CSRF_ENDPOINT }

/**
 * Get CSRF token from session storage.
 * Useful for components that need immediate access.
 */
export const getCsrfToken = (): string | null => peekCsrfToken()

/**
 * Add CSRF token to headers.
 * Useful for manual API calls.
 */
export const addCsrfToHeaders = (
  headers: Record<string, string> = {}
): Record<string, string> => {
  const token = peekCsrfToken()
  if (token) {
    headers[CSRF_HEADER] = token
  }
  return headers
}
